import React from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { useFormikContext } from 'formik';
import { resolveAutoDiscounts } from '@ourbranch/lookups';

import { Label } from 'core/components/label';
import { numberFormatter } from 'core/helpers/formatters';
import { DiscountRow } from './discount-row';

/**
 * The policy Details panel's auto discounts for a brand that declares its own list (#35626, CFM).
 * Read-only: every one is derived by the rater from underwriting/rating data (car count, homeowner,
 * pay plan, driver history) or returned by ClarionDoor (transfer, renewal), so there is nothing to
 * toggle. Resolved from the segment being edited — its cars, drivers, and the savings rows in
 * `coverages` — with the same @ourbranch/lookups resolver the offer card and documents use.
 */
const BrandAutoDiscounts = observer(function BrandAutoDiscounts({ discountIds }) {
  const { values } = useFormikContext();
  const discounts = resolveAutoDiscounts({
    discountIds,
    cars: values?.cars,
    drivers: values?.drivers,
    autoCoverages: values?.coverages
  });
  const appliedCount = discounts.filter(({ isApplied }) => isApplied).length;

  return (
    <div id="brand_auto_discounts">
      <Label type="discount">{`${appliedCount} of ${discounts.length} applied`}</Label>
      {discounts.map(({ id, label, isApplied, amount }) => (
        <DiscountRow
          key={id}
          id={id}
          label={label}
          isActive={isApplied}
          statusText={isApplied ? (amount ? `Saving $${numberFormatter(amount)}` : 'Applied') : 'Not applied'}
        />
      ))}
    </div>
  );
});

BrandAutoDiscounts.propTypes = {
  discountIds: PropTypes.arrayOf(PropTypes.string).isRequired
};

export default BrandAutoDiscounts;
